// WeeklyDecisionCard — one decision per business week, surfaced as a queued
// modal BETWEEN WEEKS (never mid-week, never mid-play). Choices show only
// their IMMEDIATE effect; the delayed consequence is hidden on purpose, so
// players learn — by living it — that good calls pay off and bad calls bite
// a few weeks later.
//
// Sims-reboot: this used to render inline on HQ. It now mounts globally in
// DailyHub and only shows when (a) there's a card for this week, (b) it's
// the between-week moment (day===1 + Payday cleared), and (c) no
// higher-priority modal is using the stage.

import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { ConsequenceEffect } from '@biz-kid/engine';
import { useGameState, useDispatch } from '@biz-kid/store';
import { Card, Eyebrow, Chip, Button, money } from '../../ui/kit.js';
import { DECK, FRAMEWORKS, type DecisionChoice } from './weeklyDecisionDeck.js';
import { useLife } from '../../life/store.js';
import { useModalSlot } from '../../ui/modalQueue.js';
import { useVoice } from '../../voice/VoiceContext.js';

function immediateChips(e?: ConsequenceEffect): { text: string; tone: 'up' | 'down' | 'neutral' }[] {
  if (!e) return [];
  const out: { text: string; tone: 'up' | 'down' | 'neutral' }[] = [];
  if (e.cash) out.push({ text: `${e.cash >= 0 ? '+' : '−'}${money(Math.abs(e.cash))} now`, tone: e.cash >= 0 ? 'up' : 'down' });
  if (e.reputation) out.push({ text: `${e.reputation >= 0 ? '+' : ''}${e.reputation} rep`, tone: e.reputation >= 0 ? 'up' : 'down' });
  return out;
}

export function WeeklyDecisionCard() {
  const week = useGameState((s) => s.clock.week);
  const seed = useGameState((s) => s.rngSeed);
  const lastResolved = useGameState((s) => s.lastDecisionWeek);
  const dayOfWeek = useGameState((s) => s.clock.dayOfWeek);
  const dispatch = useDispatch();
  const lastHarvested = useLife().lastHarvestedWeek ?? 0;

  const settledWeek = week - 1;
  const paydayDone = lastHarvested >= settledWeek;
  const hasCard = lastResolved < week;
  const betweenWeeks = dayOfWeek === 1 && paydayDone;
  // Snooze for this session if the player closes it without choosing.
  const [snoozed, setSnoozed] = useState(false);
  const wants = hasCard && betweenWeeks && !snoozed;

  // Priority 65 — between KC (60) and Consequence (70). Decisions feel
  // current-week-personal; lessons that already happened can wait.
  const { canShow } = useModalSlot('weekly-decision', wants, 65);

  // MIKO SPEAKS — announce the card and read the SITUATION aloud (young readers
  // play by ear). Keyed to the week so each card speaks exactly once, even
  // across re-renders or a snooze-and-reopen.
  const { speak } = useVoice();
  const spokeWeekRef = useRef(0);
  const showing = hasCard && canShow;
  const situation = showing ? DECK[(week + seed) % DECK.length]!.situation : null;
  useEffect(() => {
    if (showing && situation && spokeWeekRef.current !== week) {
      spokeWeekRef.current = week;
      void speak(`Decision time! ${situation} What's your move?`);
    }
  }, [showing, situation, week, speak]);

  if (!hasCard || !canShow) return null;
  const card = DECK[(week + seed) % DECK.length]!;
  const fw = FRAMEWORKS[card.framework];

  const choose = (c: DecisionChoice) => {
    dispatch({
      kind: 'ResolveDecisionCard',
      scheduleId: `${card.id}-w${week}`,
      ...(c.immediate ? { immediate: c.immediate } : {}),
      delayed: { weeksAhead: c.delayed.weeksAhead, framework: card.framework, lesson: c.delayed.lesson, effect: c.delayed.effect },
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 74,
        display: 'grid', placeItems: 'center', padding: 16,
        background: 'color-mix(in srgb, var(--bg) 70%, transparent)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <motion.div initial={{ y: 14, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} style={{ width: '100%', maxWidth: 440 }}>
        <Card style={{ borderColor: fw.color, boxShadow: `0 0 0 1px ${fw.color}, var(--shadow)` }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 28 }}>{card.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Eyebrow color={fw.color}>This week's decision</Eyebrow>
                <span style={{ fontSize: 11, fontWeight: 800, color: fw.color }}>{fw.icon} {fw.label}</span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 15.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.45 }}>{card.situation}</p>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            {card.choices.map((c) => (
              <motion.button
                key={c.label}
                type="button"
                onClick={() => choose(c)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.99 }}
                style={{ textAlign: 'left', padding: 12, borderRadius: 14, border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{c.label}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{c.sub}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {immediateChips(c.immediate).map((chip) => (
                      <span key={chip.text} style={{ fontSize: 11, fontWeight: 800, color: chip.tone === 'up' ? 'var(--up)' : 'var(--down)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 999, padding: '3px 8px' }}>{chip.text}</span>
                    ))}
                    {!c.immediate && <Chip tone="neutral">no cost now</Chip>}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, gap: 12 }}>
            <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-3)', flex: 1 }}>
              ⏳ Choices have consequences a few weeks later — for better or worse.
            </p>
            <Button tone="ghost" size="sm" onClick={() => setSnoozed(true)}>Later</Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
